import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('focusphoto_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('focusphoto_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, language = 'pl') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, language });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('focusphoto_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithSsoToken = async (ssoToken) => {
    localStorage.setItem('focusphoto_token', ssoToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${ssoToken}`;
    setToken(ssoToken);
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      localStorage.removeItem('focusphoto_token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      return { success: false, error: 'SSO login failed' };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('focusphoto_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithSsoToken, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
